/**
 * Product cross-sell: load related recommendations, variant updates, quick add.
 */

const SECTION_API_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

class ProductCrossSell extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.#abortController?.abort();
  }

  async #loadRecommendations() {
    const {
      recommendationsPerformed,
      url,
      sectionId,
      productId,
      intent = 'related',
    } = this.dataset;

    if (recommendationsPerformed === 'true' || !url || !sectionId || !productId) {
      return;
    }

    // Avoid refetching if cards already rendered from catalog fallback
    // Still upgrade to related recommendations when available
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('product_id', productId);
      requestUrl.searchParams.set('section_id', sectionId);
      requestUrl.searchParams.set('intent', intent);

      const response = await fetch(requestUrl.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector(`#${CSS.escape(this.id)}`)
        || doc.querySelector(`product-cross-sell[data-testid="product-cross-sell-block"]`);

      if (!next) return;

      // Prefer the matching block instance when multiple exist
      const nextByProduct = doc.querySelector(
        `product-cross-sell[data-product-id="${CSS.escape(productId)}"]`
      );
      const source = nextByProduct || next;
      const hasCards = source.querySelector('[data-cross-sell-card]');
      const hasExistingCards = this.querySelector('[data-cross-sell-card]');

      // Never replace a populated list with an empty recommendations response
      if (!hasCards && hasExistingCards) {
        this.dataset.recommendationsPerformed = 'true';
        this.querySelectorAll('.product-cross-sell__skeleton').forEach((el) => el.remove());
        return;
      }

      this.dataset.recommendationsPerformed = 'true';
      this.innerHTML = source.innerHTML;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('[product-cross-sell] recommendations', error);
      }
    }
  }

  #onChange = (event) => {
    const select = event.target.closest('[data-cross-sell-variant]');
    if (!select || select.tagName !== 'SELECT') return;

    const card = select.closest('[data-cross-sell-card]');
    if (!card) return;

    const option = select.options[select.selectedIndex];
    if (!option) return;

    this.#updateCard(card, {
      available: option.dataset.available === 'true',
      price: option.dataset.price || '',
      compareAtPrice: option.dataset.comparePrice || '',
      image: option.dataset.image || '',
      url: option.dataset.url || '',
    });
  };

  #onClick = async (event) => {
    const button = event.target.closest('[data-cross-sell-add]');
    if (!button || button.disabled) return;

    const card = button.closest('[data-cross-sell-card]');
    if (!card) return;

    const variantInput = card.querySelector('[data-cross-sell-variant]');
    const variantId = variantInput?.value;
    if (!variantId) return;

    await this.#addToCart(card, button, Number(variantId));
  };

  #updateCard(card, { available, price, compareAtPrice, image, url }) {
    const priceEl = card.querySelector('[data-cross-sell-price]');
    if (priceEl && price) {
      if (compareAtPrice) {
        priceEl.innerHTML = `<span class="cross-sell-card__sale-price">${price}</span><s class="cross-sell-card__compare-price">${compareAtPrice}</s>`;
      } else {
        priceEl.innerHTML = `<span class="cross-sell-card__regular-price">${price}</span>`;
      }
    }

    const imageEl = card.querySelector('.cross-sell-card__image');
    if (imageEl && image && imageEl.tagName === 'IMG') {
      imageEl.src = image;
      imageEl.removeAttribute('srcset');
    }

    if (url) {
      card.querySelectorAll('a[href]').forEach((link) => {
        link.href = url;
      });
    }

    const addButton = card.querySelector('[data-cross-sell-add]');
    if (addButton) {
      addButton.disabled = !available;
    }
  }

  async #addToCart(card, button, variantId) {
    const label = button.querySelector('[data-cross-sell-add-label]');
    const busy = button.querySelector('[data-cross-sell-add-busy]');

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.hidden = true;
    if (busy) busy.hidden = false;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: SECTION_API_HEADERS,
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this.#cartSections(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.description || data?.message || 'Error adding to cart');
      }

      this.#publishCartUpdate(data);
      this.#flashAdded(button, label, busy);
    } catch (error) {
      console.error('[product-cross-sell]', error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (label) label.hidden = false;
      if (busy) busy.hidden = true;
    }
  }

  #cartSections() {
    const sections = [];

    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.getAttribute('data-section-id');
      if (id && !sections.includes(id)) sections.push(id);
    });

    // Fallback: any section ids on the page (header cart, etc.)
    if (sections.length === 0) {
      document.querySelectorAll('[data-section-id]').forEach((el) => {
        const id = el.getAttribute('data-section-id');
        if (id && !sections.includes(id)) sections.push(id);
      });
    }

    return sections.slice(0, 5);
  }

  #publishCartUpdate(data) {
    const detail = {
      source: 'product-cross-sell',
      data,
      cartData: data,
      sections: data?.sections,
    };

    document.dispatchEvent(new CustomEvent('cart:update', { detail, bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail, bubbles: true }));

    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, detail);
    }

    this.#openCartDrawer();
  }

  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;

    // Already open (e.g. adding from inside the drawer) — skip
    const dialog = drawer.querySelector('dialog[ref="dialog"], dialog.cart-drawer__dialog');
    if (dialog?.open) return;

    if (this.dataset.context === 'cart-drawer') return;

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    const trigger =
      drawer.querySelector('[data-testid="cart-drawer-trigger"]') ||
      document.querySelector('[data-testid="cart-drawer-trigger"]');
    trigger?.click();
  }

  #flashAdded(button, label, busy) {
    if (busy) busy.hidden = true;
    if (label) {
      label.hidden = false;
      const original = label.innerHTML;
      label.textContent = label.dataset.addedLabel || 'Added';
      window.setTimeout(() => {
        label.innerHTML = original;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1200);
    } else {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('product-cross-sell')) {
  customElements.define('product-cross-sell', ProductCrossSell);
}